/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

/** "Liberar o plano completo" (RN-10): what each answer of the activate-now route means. */

import type { ActivateNowResponse } from '@services/requests/housien-services/AgenciesService.subscription';

export type ActivatePhase =
  | 'confirm' | 'processing' | 'success' | 'declined'
  | 'retry' | 'missingCard' | 'notInTrial' | 'error';

/** Where the dialog stands, and what the last answer said. */
export interface ActivateState {
  phase: ActivatePhase;
  chargedCents?: number | null;
  cycleEnd?: string;
  reason?: string;
  snapshot?: boolean;
}

type Body = Partial<ActivateNowResponse> | undefined;

const FROM_RESULT: Record<string, (body: NonNullable<Body>) => ActivateState> = {
  activated: (body) => ({
    phase: 'success', chargedCents: body.charged_amount_cents, cycleEnd: body.current_cycle_end_date,
  }),
  payment_declined: (body) => ({ phase: 'declined', reason: body.decline_message }),
  activation_in_progress: () => ({ phase: 'retry' }),
  not_in_trial: () => ({ phase: 'notInTrial' }),
  payment_method_missing: () => ({ phase: 'missingCard' }),
  billing_snapshot_missing: () => ({ phase: 'error', snapshot: true }),
};

/**
 * What an answer of the activate-now route turns into.
 *
 * @param body - The `result`-carrying body, of a 200 or of an error answer.
 * @returns The state the dialog moves to; unknown answers are a plain error.
 */
export function stateFromResponse(body: Body): ActivateState {
  const build = body?.result ? FROM_RESULT[body.result] : undefined;
  return build && body ? build(body) : { phase: 'error' };
}
